import { type JSX, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Schliesskreuz } from './Schliesskreuz';
import { Sinnbild } from './Sinnbild';
import { sichtbareBereiche } from './Rahmen';
import type { Benutzer, Recht } from '../api/anmeldung';
import { MASKENHILFE, ablaeufeZu, schrittziel } from '../maskenhilfe';
import { WEGE, type Maske } from '../wegweiser';

/** Abstand der Fläche unter dem Symbol, wie bei der Liste der Glocke (px). */
const ABSTAND = 4;
/** Mindestabstand zum Bildschirmrand (px). */
const RAND = 8;

/**
 * Die Hilfe zur Maske — ein Aufklapper unter dem Hilfe-Symbol der Kopfzeile
 * (Auftrag CRM-51, ÄA-46).
 *
 * NFA-17 verlangt „kontextbezogene Hilfe in der Oberfläche". Kontextbezogen
 * heißt hier: Sie weiß, auf welcher Maske sie steht, und rechnet daraus, wo im
 * Ablauf man ist — eine Hilfe, die für alle Masken dasselbe sagt, erfüllt das
 * Wort, aber nicht die Anforderung.
 *
 * ============================================================================
 * WARUM UNTER DEM TITEL KEINE ZEILE MEHR STEHT (04.10.2026)
 * ============================================================================
 *
 * Auftraggeber: „Im crm den Hinweistext oben entfernen und unter das
 * hilfeicon oben im Header nutzen".
 *
 * Vom 13.09.2026 an stand unter jedem Titel ein Satz mit „Mehr dazu" — für
 * den, der nicht sucht. Auf einer Maske, die man täglich öffnet, liest man ihn
 * einmal und überliest ihn danach; er stand aber jedes Mal zwischen Seitenkopf
 * und Arbeit. Der Satz ist jetzt die Einleitung des Aufklappers.
 *
 * NFA-17 BLEIBT ERFÜLLT: Der Ort der Hilfe hat gewechselt, ihr Bezug nicht.
 *
 *   - Das Symbol steht auf JEDER Maske an derselben Stelle, und F1 wirkt
 *     überall. `MASKENHILFE` ist ein `Record` über alle Masken: Eine Maske
 *     ohne Hilfe kommt nicht durch den Übersetzer.
 *   - Der Inhalt gehört zu der Maske, auf der man steht — die Einleitung, was
 *     man hier tut, die Stelle in jedem Ablauf, was hier nicht geht.
 *   - Ungefragt steht die Hilfe weiter dort, wo die Fehler entstehen: am Feld
 *     (`feld__hilfe` in `Feld.tsx`).
 *
 * ============================================================================
 * DREI ABSCHNITTE, UND DER DRITTE IST DER NÜTZLICHSTE
 * ============================================================================
 *
 *   WAS SIE HIER TUN — zwei, drei Sätze.
 *   WO IM ABLAUF — die Kette mit „Sie sind hier"; jeder Schritt ist ein Weg.
 *   WAS HIER NICHT GEHT — mit dem Verweis dorthin, wo es hingehört.
 *
 * Der dritte spart im Betrieb die Zeit: Fast jede Rückfrage lautet „wo trage
 * ich X ein" und nicht „wie bediene ich Y".
 *
 * ============================================================================
 * EIN AUFKLAPPER WIE GLOCKE UND BENUTZER, KEIN DIALOG
 * ============================================================================
 *
 * Das Muster ist die Offenlegung (`aria-expanded`) wie bei den übrigen
 * Aufklappern der Kopfzeile (`Aufklapper.tsx`). Die Maske dahinter bleibt
 * bedienbar, ein Schleier wäre für eine Auskunft zu viel. Wie „Neu", Glocke
 * und Benutzermenü öffnet er nur auf Verlangen und nicht beim Überfahren: Ein
 * Symbol der Kopfzeile streift die Maus auf dem Weg zu den anderen.
 *
 * Eine eigene Komponente und nicht `Aufklapper` mit Einträgen: Der Inhalt sind
 * Absätze mit Wegen darin und keine Liste von Befehlen. Und der Fokus geht bei
 * F1 auf die Fläche selbst und nicht auf den ersten Weg — ein Vorleseprogramm
 * liest dann ihren Namen, und Enter löst nicht versehentlich einen Sprung aus.
 * Aussehen und Bedienung sind die des Aufklappers (NFA-07, NFA-14):
 *
 *   ÖFFNEN — Klick, Tippen, Enter oder Leertaste auf dem Symbol; der Fokus
 *   bleibt dort, und Tab führt hinein. Pfeil nach unten auf dem Symbol und F1
 *   von überall öffnen und setzen den Fokus in die Fläche.
 *
 *   SCHLIESSEN — Escape gibt den Fokus dorthin zurück, wo er vor dem Öffnen
 *   war: an das Symbol oder an das Feld, in dem F1 gedrückt wurde (WCAG 2.4.3).
 *   Ein zweiter Klick auf das Symbol, Tab hinaus und ein Klick oder Tippen
 *   daneben schließen, ohne den Fokus zu versetzen — dort hat man ihn gerade
 *   selbst hingesetzt. Ein Weg schließt und führt in die Maske, und die setzt
 *   den Fokus auf ihren Titel.
 *
 * ============================================================================
 * EINE MASKE OHNE EIGENE HILFE HAT KEIN SYMBOL
 * ============================================================================
 *
 * In der Arbeitsfläche gibt es keine — der `Record` oben verlangt jede. Steht
 * der Rahmen einmal ohne Maskenhilfe, bleibt sein Platz `hilfe` leer: Das
 * Symbol fehlt, und F1 tut nichts. Ein Symbol, das nur sagt, dass es hier
 * nichts zu sagen hat, wäre ein Klick ohne Auskunft. Dieselbe Regel gilt für
 * die Glocke: Was nichts bietet, steht nicht da.
 *
 * ============================================================================
 * F1 ÖFFNET SIE AUCH
 * ============================================================================
 *
 * Es ist die Taste, die jeder für Hilfe hält, und sie kostet nichts: Der
 * Browser belegt sie mit seiner eigenen Hilfe, die in einer Anwendung niemand
 * meint. Der Fänger sitzt am Fenster dieser Anwendung und nicht am Dokument —
 * er greift, solange die Arbeitsfläche steht.
 */
export function Maskenhilfe({
  maske,
  titel,
  rechte,
  kontoart = 'person',
  beiMaske,
}: {
  readonly maske: Maske;
  /**
   * Der Titel der Maske, wie er im Seitenkopf steht.
   *
   * Die Fläche beginnt mit ihm, weil F1 überall gedrückt wird und die Antwort
   * mit dem Ort anfangen muss.
   */
  readonly titel: string;
  /** Die Rechte des Angemeldeten — sie entscheiden, welcher Verweis ein Weg ist. */
  readonly rechte: readonly Recht[];
  /** BP-02 (Auftrag #32): Das Prueferkonto erreicht nur die Pruefflaeche — die Hilfe zeigt keinen Weg daneben. */
  readonly kontoart?: Benutzer['kontoart'];
  /** Ein Weg der Hilfe führt dorthin — sie schließt sich dabei. */
  readonly beiMaske: (ziel: Maske) => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);

  const hilfe = MASKENHILFE[maske.art];
  const ablaeufe = ablaeufeZu(maske.art);
  const kennung = useId();
  const flaecheId = `${kennung}-hilfe`;
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const flaeche = useRef<HTMLDivElement>(null);

  /*
   * WOHIN DER FOKUS ZURÜCKGEHT. `herkunft` ist das Element, das ihn beim
   * Öffnen hatte — das Symbol oder das Feld, in dem F1 gedrückt wurde. Hatte
   * ihn keines (Safari setzt ihn beim Klick nicht auf den Knopf), ist es das
   * Symbol.
   */
  const herkunft = useRef<HTMLElement | null>(null);
  /* F1 und Pfeil nach unten: Der Fokus soll in die Fläche, sobald sie steht. */
  const fokusHinein = useRef(false);

  function oeffnen(hinein: boolean): void {
    if (offen) {
      if (hinein) flaeche.current?.focus();
      return;
    }
    const aktiv = document.activeElement;
    herkunft.current = aktiv instanceof HTMLElement && aktiv !== document.body ? aktiv : null;
    fokusHinein.current = hinein;
    setzeOffen(true);
  }

  function schliessen(fokusZurueck: boolean): void {
    fokusHinein.current = false;
    setzeOffen(false);
    if (!fokusZurueck) return;
    const ziel = herkunft.current;
    (ziel?.isConnected === true ? ziel : knopf.current)?.focus();
  }

  /*
   * WAS MAN NICHT SEHEN DARF, WIRD GENANNT — ABER NICHT VERLINKT (4.3, BEN-08).
   *
   * Ein Trainer liest im Ablauf des Betriebstags, dass danach Trainer angefragt
   * werden; das gehört zur Auskunft, und es zu verschweigen hieße, ihm den
   * Zusammenhang zu nehmen. Ein KNOPF dorthin wäre dagegen ein Versprechen, das
   * die Maske nicht hält: Er landete auf einer Seite, die ihm die Daten
   * verweigert. Also bleibt der Text, und der Weg fällt weg.
   *
   * `bereich: null` heißt „hängt an keinem Menüpunkt" (Mein Konto) — das ist
   * kein fehlendes Recht, sondern keines nötig.
   */
  const erlaubt = useMemo(
    () => new Set(sichtbareBereiche(rechte, kontoart).map((b) => b.kennung)),
    [rechte, kontoart],
  );
  const erreichbar = (art: Maske['art']): boolean => {
    const bereich = WEGE[art].bereich;
    return bereich === null || erlaubt.has(bereich);
  };

  /* F1 öffnet von überall (NFA-17). Über eine Ref, damit der Fänger nur einmal sitzt. */
  const beiF1 = useRef<() => void>(() => undefined);
  useEffect(() => {
    beiF1.current = () => oeffnen(true);
  });
  useEffect(() => {
    function beiTaste(e: KeyboardEvent): void {
      if (e.key === 'F1') {
        e.preventDefault();
        beiF1.current();
      }
    }
    window.addEventListener('keydown', beiTaste);
    return () => {
      window.removeEventListener('keydown', beiTaste);
    };
  }, []);

  /* Der Maskenwechsel schließt die Hilfe: Sie gehört zu der, die man verlässt. */
  useEffect(() => {
    fokusHinein.current = false;
    setzeOffen(false);
  }, [maske.art]);

  /* Mit F1 oder Pfeil nach unten geöffnet: Der Fokus geht in die Fläche. */
  useEffect(() => {
    if (!offen || !fokusHinein.current) return;
    fokusHinein.current = false;
    flaeche.current?.focus();
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    /*
     * ESCAPE AUCH OHNE FOKUS IN DER HÜLLE: Nach einem Klick in Safari steht er
     * nirgends. Was ein Dialog oder ein anderer Aufklapper schon genommen hat,
     * ist `defaultPrevented` und gilt dort.
     */
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      schliessen(true);
    };
    /* Daneben geklickt oder getippt: zu. Symbol und Fläche liegen in der Hülle und zählen als drinnen. */
    const beiDruck = (e: PointerEvent): void => {
      if (huelle.current?.contains(e.target as Node) !== true) schliessen(false);
    };
    window.addEventListener('keydown', beiTaste);
    document.addEventListener('pointerdown', beiDruck);
    return () => {
      window.removeEventListener('keydown', beiTaste);
      document.removeEventListener('pointerdown', beiDruck);
    };
  }, [offen]);

  /*
   * DIE FLÄCHE PASST IN DEN BILDSCHIRM — AUCH AUF DEM IPAD.
   *
   * Sie hängt rechtsbündig unter dem Symbol wie die Liste der Glocke. Wäre sie
   * dort zu breit, rückt sie nach links, aber nie über den Rand hinaus; breiter
   * als der Bildschirm abzüglich Rand wird sie nicht (`stil.css`). Ihre Höhe
   * endet acht Pixel über dem unteren Rand, und was nicht passt, rollt innen.
   * Gerechnet wird mit `innerHeight` und nicht mit `100vh`: Auf dem iPad zählt
   * `100vh` die Leiste des Browsers mit, und der Fuß der Hilfe läge darunter.
   * Dreht man das Gerät, legt sie sich neu, statt zu schließen.
   *
   * Die Lage geht vor dem Zeichnen unmittelbar an das Element: Über den Zustand
   * gäbe es ein Bild an der falschen Stelle, oder die Fläche wäre unsichtbar,
   * wenn F1 den Fokus hineinsetzt — und ein unsichtbares Element nimmt keinen.
   */
  useLayoutEffect(() => {
    if (!offen) return;
    const legen = (): void => {
      const r = knopf.current?.getBoundingClientRect();
      const f = flaeche.current;
      if (r === undefined || f === null) return;
      const breite = f.offsetWidth;
      const oben = r.bottom + ABSTAND;
      const links = Math.min(
        Math.max(RAND, r.right - breite),
        Math.max(RAND, window.innerWidth - RAND - breite),
      );
      f.style.top = `${oben}px`;
      f.style.left = `${links}px`;
      f.style.maxHeight = `${Math.max(0, window.innerHeight - oben - RAND)}px`;
    };
    legen();
    window.addEventListener('resize', legen);
    return () => {
      window.removeEventListener('resize', legen);
    };
  }, [offen]);

  return (
    <div
      ref={huelle}
      className="aufklapper"
      onBlur={(e) => {
        /*
         * Mit Tab hinaus: zu, ohne den Fokus zurückzuholen. Nur bei einem neuen
         * Ziel — ohne eines (Safari beim Klick auf das Symbol, der Wechsel in
         * ein anderes Fenster) entscheidet der Zeiger.
         */
        const ziel = e.relatedTarget;
        if (offen && ziel !== null && !e.currentTarget.contains(ziel)) schliessen(false);
      }}
    >
      <button
        ref={knopf}
        type="button"
        className="kopfzeile__symbol"
        title="Hilfe zu dieser Maske (F1)"
        aria-label="Hilfe zu dieser Maske"
        aria-keyshortcuts="F1"
        aria-expanded={offen}
        aria-controls={offen ? flaecheId : undefined}
        onClick={() => {
          if (offen) schliessen(false);
          else oeffnen(false);
        }}
        onKeyDown={(e) => {
          /* Pfeil nach unten öffnet und geht hinein, wie bei Glocke und Benutzer. */
          if (e.key !== 'ArrowDown') return;
          e.preventDefault();
          oeffnen(true);
        }}
      >
        <Sinnbild name="question" gewicht="light" />
      </button>

      {offen && (
        <div
          id={flaecheId}
          ref={flaeche}
          className="aufklapper__liste maskenhilfe"
          role="region"
          aria-label={`Hilfe zu ${titel}`}
          tabIndex={-1}
        >
          <h2 className="maskenhilfe__titel">{titel}</h2>
          <p className="maskenhilfe__einleitung">{hilfe.einleitung}</p>

          <section>
            <h3>Was Sie hier tun</h3>
            {hilfe.tut.map((satz) => (
              <p key={satz}>{satz}</p>
            ))}
          </section>

          {/*
            WO IM ABLAUF — gerechnet und nicht je Maske aufgeschrieben.

            „In welchen Prozessen stellt sie welchen Schritt dar": Die Antwort
            steht in den Ketten (`PROZESSE`), und die Hilfe liest daraus ab,
            der wievielte Schritt diese Maske ist. Eine Maske, die ihre Stelle
            selbst trüge, hätte sie beim ersten Umbau falsch. Eine Maske ohne
            Ablauf ist ein Ort und bekommt keinen leeren Abschnitt.
          */}
          {ablaeufe.length > 0 && (
            <section>
              <h3>Wo im Ablauf</h3>
              {ablaeufe.map(({ prozess, stelle }) => (
                <div key={prozess.name}>
                  <h4>
                    „{prozess.name}": Schritt {stelle + 1} von {prozess.schritte.length}
                  </h4>
                  <p className="hilfe">{prozess.zweck}</p>
                  <ol className="maskenhilfe__kette">
                    {prozess.schritte.map((schritt, nr) => {
                      /* CRM-118 (L7): ein Schritt auf eine Akte führt in ihre Liste. */
                      const ziel = schrittziel(schritt.art);
                      return (
                        <li
                          key={schritt.art}
                          className={nr === stelle ? 'maskenhilfe__schritt--hier' : undefined}
                        >
                          {nr === stelle ? (
                            <>
                              <strong>{schritt.was}</strong>{' '}
                              <span className="marke">Sie sind hier</span>
                            </>
                          ) : ziel !== undefined && erreichbar(ziel.art) ? (
                            /*
                              JEDER ANDERE SCHRITT IST EIN WEG. Eine Kette, die nur
                              beschreibt, lässt den Leser die Maske suchen, die sie
                              gerade genannt hat.
                            */
                            <button
                              type="button"
                              className="knopf knopf--verweis"
                              onClick={() => {
                                schliessen(false);
                                beiMaske(ziel);
                              }}
                            >
                              {schritt.was}
                            </button>
                          ) : (
                            schritt.was
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              ))}
            </section>
          )}

          {hilfe.nicht !== undefined && hilfe.nicht.length > 0 && (
            <section>
              <h3>Was hier nicht geht</h3>
              <ul className="maskenhilfe__verweise">
                {hilfe.nicht.map((v) => (
                  <li key={v.text}>
                    {v.maske === undefined || !erreichbar(v.maske.art) ? (
                      v.text
                    ) : (
                      <button
                        type="button"
                        className="knopf knopf--verweis"
                        onClick={() => {
                          const ziel = v.maske;
                          schliessen(false);
                          if (ziel !== undefined) beiMaske(ziel);
                        }}
                      >
                        {v.text}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="hilfe">
            Diese Hilfe öffnet sich auch mit <span className="taste">F1</span>.
          </p>

          {/*
            DAS KREUZ (Auftrag CRM-102) steht oben rechts neben dem Titel, im
            Quelltext aber zuletzt: Tab führt zuerst in die Hilfe und erst dann
            auf das Kreuz. Es gibt den Fokus zurück, wo er vor dem Öffnen
            stand, wie Esc.
          */}
          <Schliesskreuz beiKlick={() => schliessen(true)} />
        </div>
      )}
    </div>
  );
}
